/**
 * @repo/api-client — Type-safe API client for the web frontend.
 * All requests go through /api/* — secrets never leave the worker.
 */
import type {
  AuthRequest,
  AuthResponse,
  SignUpRequest,
  UserProfile,
  Post,
  PostListResponse,
  PostsQuery,
  Product,
  ProductListResponse,
  ProductsQuery,
  Category,
  Cart,
  CheckoutRequest,
  Order,
  PracticeSession,
  CreatePracticeSessionRequest,
  PracticeStats,
  PracticeLibraryResponse,
  PracticeRecommendationsResponse,
  MoodEntry,
  CreateMoodEntryRequest,
  MoodRecommendationsResponse,
  UserBadge,
  MilestoneCheckResponse,
  MembershipTierResponse,
  UserPass,
  QrTokenResponse,
  VerifyQrRequest,
  VerifyQrResponse,
  AdminDashboardResponse,
  AdminUserRoleUpdateRequest,
  SiteSettingsRecord,
  GuidedPractice,
  GuidedPracticeInput,
  HealthResponse,
  ApiError,
  Event,
  TicketTier,
  EventRegistration,
  CheckInRequest,
  CheckInResponse,
  PosItem,
  PosOrderLine,
  PosOrder,
  Donation,
  DonationRequest,
  VolunteerApplication,
  Course,
  CourseLesson,
  CourseEnrollment,
  TeacherProfile,
  Book,
  BookReview,
  WisdomVideo,
  CommunityStory,
  StorySubmissionRequest,
} from "@repo/shared-types";

export class ApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string = "") {
    this.baseUrl = baseUrl;
  }

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const res = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
      credentials: "include",
    });

    if (!res.ok) {
      const payload = (await res.json().catch(() => ({
        error: `HTTP ${res.status}: ${res.statusText}`,
      }))) as Partial<ApiError>;
      const error: ApiError = {
        error: payload.error ?? `HTTP ${res.status}: ${res.statusText}`,
        message: payload.message,
      };
      throw new ApiClientError(error.error, res.status);
    }

    return res.json() as Promise<T>;
  }

  private withQuery<T extends object>(path: string, query?: T): string {
    if (!query) return path;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
      if (typeof value === "string" && value !== "") {
        params.set(key, value);
      }
    }
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
  }

  // ── Auth ──
  async signUp(data: SignUpRequest): Promise<AuthResponse> {
    return this.request("/api/auth/signup", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async login(data: AuthRequest): Promise<AuthResponse> {
    return this.request("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async refreshToken(): Promise<AuthResponse> {
    return this.request("/api/auth/refresh", { method: "POST" });
  }

  async logout(): Promise<{ success: boolean }> {
    return this.request("/api/auth/logout", { method: "POST" });
  }

  async getProfile(): Promise<UserProfile> {
    return this.request("/api/auth/profile");
  }

  async updateProfile(data: Partial<UserProfile>): Promise<UserProfile> {
    return this.request("/api/auth/profile", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  // ── Content ──
  async listPosts(query?: PostsQuery): Promise<PostListResponse> {
    return this.request(this.withQuery("/api/content/posts", query));
  }

  async getPost(slug: string): Promise<Post> {
    return this.request(`/api/content/posts/${encodeURIComponent(slug)}`);
  }

  async listPages(): Promise<Post[]> {
    return this.request("/api/content/pages");
  }

  // ── Products ──
  async listProducts(query?: ProductsQuery): Promise<ProductListResponse> {
    return this.request(this.withQuery("/api/products", query));
  }

  async getProduct(id: number | string): Promise<Product> {
    return this.request(`/api/products/${id}`);
  }

  async listCategories(): Promise<Category[]> {
    return this.request("/api/products/categories");
  }

  // ── Cart ──
  async getCart(): Promise<Cart> {
    return this.request("/api/cart");
  }

  async addToCart(productId: number, quantity = 1): Promise<Cart> {
    return this.request("/api/cart/items", {
      method: "POST",
      body: JSON.stringify({ product_id: productId, quantity }),
    });
  }

  async updateCartItem(productId: number, quantity: number): Promise<Cart> {
    return this.request(`/api/cart/items/${productId}`, {
      method: "PUT",
      body: JSON.stringify({ quantity }),
    });
  }

  async clearCart(): Promise<{ success: boolean }> {
    return this.request("/api/cart", { method: "DELETE" });
  }

  // ── Checkout ──
  async createOrder(data: CheckoutRequest): Promise<Order> {
    return this.request("/api/checkout", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getOrder(orderId: string): Promise<Order> {
    return this.request(`/api/checkout/${orderId}`);
  }

  // ── Practice ──
  async listPracticeSessions(limit = 90): Promise<PracticeSession[]> {
    return this.request(this.withQuery("/api/practice/sessions", { limit: String(limit) }));
  }

  async createPracticeSession(data: CreatePracticeSessionRequest): Promise<PracticeSession> {
    return this.request("/api/practice/sessions", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getPracticeStats(): Promise<PracticeStats> {
    return this.request("/api/practice/stats");
  }

  async getPracticeLibrary(): Promise<PracticeLibraryResponse> {
    return this.request("/api/practice/library");
  }

  async getPracticeRecommendations(): Promise<PracticeRecommendationsResponse> {
    return this.request("/api/practice/recommendations");
  }

  // ── Mood ──
  async listMoodHistory(limit = 50): Promise<MoodEntry[]> {
    return this.request(this.withQuery("/api/mood/history", { limit: String(limit) }));
  }

  async saveMoodEntry(data: CreateMoodEntryRequest): Promise<MoodEntry> {
    return this.request("/api/mood/assess", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getMoodRecommendations(data: CreateMoodEntryRequest & { mood_entry_id?: string }): Promise<MoodRecommendationsResponse> {
    return this.request("/api/mood/recommendations", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // ── Gamification ──
  async listBadges(): Promise<UserBadge[]> {
    return this.request("/api/gamification/badges");
  }

  async checkMilestones(): Promise<MilestoneCheckResponse> {
    return this.request("/api/gamification/check-milestone", {
      method: "POST",
    });
  }

  // ── Membership / Passes ──
  async getMembershipTier(): Promise<MembershipTierResponse> {
    return this.request("/api/membership/tier");
  }

  async listPasses(): Promise<UserPass[]> {
    return this.request("/api/membership/passes");
  }

  async generatePassQr(passId: string): Promise<QrTokenResponse> {
    return this.request(`/api/membership/pass/${encodeURIComponent(passId)}/qr`, {
      method: "POST",
    });
  }

  async verifyPassQr(payload: VerifyQrRequest): Promise<VerifyQrResponse> {
    return this.request("/api/membership/verify-qr", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // ── Health ──
  async health(): Promise<HealthResponse> {
    return this.request("/api/health");
  }

  async getPublicSiteSettings(): Promise<SiteSettingsRecord> {
    return this.request("/api/health/site-settings");
  }

  // ── Admin ──
  async getAdminDashboard(): Promise<AdminDashboardResponse> {
    return this.request("/api/admin/dashboard");
  }

  async updateAdminUserRole(
    userId: string,
    data: AdminUserRoleUpdateRequest
  ): Promise<{ success: boolean }> {
    return this.request(`/api/admin/users/${encodeURIComponent(userId)}/role`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async getAdminSettings(): Promise<SiteSettingsRecord> {
    return this.request("/api/admin/settings");
  }

  async updateAdminSettings(data: SiteSettingsRecord): Promise<SiteSettingsRecord> {
    return this.request("/api/admin/settings", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async listAdminGuidedPractices(): Promise<GuidedPractice[]> {
    return this.request("/api/admin/guided-practices");
  }

  async createAdminGuidedPractice(data: GuidedPracticeInput): Promise<GuidedPractice> {
    return this.request("/api/admin/guided-practices", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateAdminGuidedPractice(practiceId: string, data: GuidedPracticeInput): Promise<GuidedPractice> {
    return this.request(`/api/admin/guided-practices/${encodeURIComponent(practiceId)}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteAdminGuidedPractice(practiceId: string): Promise<{ success: boolean }> {
    return this.request(`/api/admin/guided-practices/${encodeURIComponent(practiceId)}`, {
      method: "DELETE",
    });
  }

  async getGuidedPractices(): Promise<GuidedPractice[]> {
    return this.request("/api/practice/guided");
  }

  // ── Events ──
  async listEvents(query?: { status?: string; type?: string }): Promise<{ events: Event[] }> {
    return this.request(this.withQuery("/api/events", query));
  }

  async getEvent(slug: string): Promise<{ event: Event; ticket_tiers: TicketTier[] }> {
    return this.request(`/api/events/${encodeURIComponent(slug)}`);
  }

  async getEventTiers(slug: string): Promise<{ ticket_tiers: TicketTier[] }> {
    return this.request(`/api/events/${encodeURIComponent(slug)}/tiers`);
  }

  async adminCreateEvent(data: Partial<Event>): Promise<Event> {
    return this.request("/api/events", { method: "POST", body: JSON.stringify(data) });
  }

  async adminDeleteEvent(id: string): Promise<{ success: boolean }> {
    return this.request(`/api/events/${encodeURIComponent(id)}`, { method: "DELETE" });
  }

  async adminUpsertTicketTier(data: Partial<TicketTier>): Promise<TicketTier> {
    return this.request("/api/events/tiers", { method: "POST", body: JSON.stringify(data) });
  }

  // ── Check-In (Sena/Admin) ──
  async listCheckIns(eventId: string, q?: string): Promise<{
    attendees: CheckInResponse[];
    stats: { total: number; checked_in: number; pending: number; walk_ins: number };
  }> {
    return this.request(this.withQuery(`/api/checkin/${encodeURIComponent(eventId)}`, q ? { q } : undefined));
  }

  async performCheckIn(eventId: string, recordId: string): Promise<CheckInResponse> {
    return this.request(`/api/checkin/${encodeURIComponent(eventId)}/${encodeURIComponent(recordId)}/check-in`, {
      method: "POST",
    });
  }

  async addWalkIn(eventId: string, data: { name: string; email: string; phone?: string; ticket_tier: string }): Promise<EventRegistration> {
    return this.request(`/api/checkin/${encodeURIComponent(eventId)}/walk-in`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // ── POS (Sena/Admin) ──
  async listPosItems(category?: string): Promise<{ items: PosItem[] }> {
    return this.request(this.withQuery("/api/pos/items", category ? { category } : undefined));
  }

  async adminUpsertPosItem(data: Partial<PosItem>): Promise<PosItem> {
    return this.request("/api/pos/items", { method: "POST", body: JSON.stringify(data) });
  }

  async listPosOrders(): Promise<{ orders: PosOrder[] }> {
    return this.request("/api/pos/orders");
  }

  async createPosOrder(data: {
    event_id?: string;
    lines: PosOrderLine[];
    delivery_fee: number;
    payment_method: PosOrder["payment_method"];
  }): Promise<PosOrder> {
    return this.request("/api/pos/orders", { method: "POST", body: JSON.stringify(data) });
  }

  // ── Donations ──
  async listDonationChannels(region?: string): Promise<{ channels: Array<{ id: string; region: string; label: string; method: string; handle: string; note?: string; active: boolean }> }> {
    return this.request(this.withQuery("/api/donations/channels", region ? { region } : undefined));
  }

  async adminUpdateDonationChannels(channels: unknown[]): Promise<{ channels: unknown[] }> {
    return this.request("/api/donations/channels", { method: "PUT", body: JSON.stringify({ channels }) });
  }

  async submitDonation(data: DonationRequest): Promise<{ id: string; message: string }> {
    return this.request("/api/donations", { method: "POST", body: JSON.stringify(data) });
  }

  // ── Volunteer ──
  async submitVolunteerApplication(data: {
    name: string;
    email: string;
    phone?: string;
    roles: string[];
    message?: string;
  }): Promise<VolunteerApplication> {
    return this.request("/api/volunteer", { method: "POST", body: JSON.stringify(data) });
  }

  async adminListVolunteerApplications(): Promise<{ applications: VolunteerApplication[] }> {
    return this.request("/api/volunteer");
  }

  // ── Courses ──
  async listCourses(filters?: { category?: string; access?: string; teacher_id?: string }): Promise<{ courses: Course[] }> {
    return this.request(this.withQuery("/api/courses", filters));
  }

  async getCourse(slug: string): Promise<{ course: Course; lessons: CourseLesson[] }> {
    return this.request(`/api/courses/${encodeURIComponent(slug)}`);
  }

  async adminUpsertCourse(data: Partial<Course> & { id: string }): Promise<Course> {
    return this.request("/api/courses", { method: "POST", body: JSON.stringify(data) });
  }

  // ── Teachers ──
  async listTeachers(): Promise<{ teachers: TeacherProfile[] }> {
    return this.request("/api/teachers");
  }

  async getTeacher(slug: string): Promise<{ teacher: TeacherProfile; courses: Course[]; books: Book[]; videos: WisdomVideo[] }> {
    return this.request(`/api/teachers/${encodeURIComponent(slug)}`);
  }

  // ── Books ──
  async listBooks(filters?: { category?: string; author_id?: string }): Promise<{ books: Book[] }> {
    return this.request(this.withQuery("/api/library/books", filters));
  }

  async getBook(slug: string): Promise<{ book: Book; reviews: BookReview[] }> {
    return this.request(`/api/library/books/${encodeURIComponent(slug)}`);
  }

  async submitBookReview(bookId: string, data: { reviewer_name: string; rating: number; body: string }): Promise<{ id: string; message: string }> {
    return this.request(`/api/library/books/${encodeURIComponent(bookId)}/reviews`, { method: "POST", body: JSON.stringify(data) });
  }

  // ── Videos ──
  async listVideos(filters?: { category?: string; access?: string; teacher_id?: string }): Promise<{ videos: WisdomVideo[] }> {
    return this.request(this.withQuery("/api/library/videos", filters));
  }

  async getVideo(slug: string): Promise<WisdomVideo> {
    return this.request(`/api/library/videos/${encodeURIComponent(slug)}`);
  }

  // ── Community Stories ──
  async listStories(filters?: { category?: string; featured?: boolean }): Promise<{ stories: CommunityStory[] }> {
    const q = filters ? { ...filters, featured: filters.featured ? "true" : undefined } : undefined;
    return this.request(this.withQuery("/api/library/stories", q as Record<string, string | undefined>));
  }

  async getStory(slug: string): Promise<CommunityStory> {
    return this.request(`/api/library/stories/${encodeURIComponent(slug)}`);
  }

  async submitStory(data: StorySubmissionRequest): Promise<{ id: string; message: string }> {
    return this.request("/api/library/stories", { method: "POST", body: JSON.stringify(data) });
  }

  async adminListAllStories(): Promise<{ stories: CommunityStory[] }> {
    return this.request("/api/library/stories/admin/all");
  }

  async adminUpdateStoryStatus(id: string, status: CommunityStory["status"]): Promise<CommunityStory> {
    return this.request(`/api/library/stories/${encodeURIComponent(id)}/status`, { method: "PATCH", body: JSON.stringify({ status }) });
  }
}

export class ApiClientError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
  }
}

// Default singleton
export const api = new ApiClient();
